import React from "react";
import { FormattedMessage } from "../../util/reactIntl";
import css from "./ListingPage.module.css";
import SeoVenueLinkMaybe, { venueDisplayNameFromPublicData } from './SeoVenueLinkMaybe';
import SeoCityLinkMaybe, { cityDisplayNameFromPublicData } from './SeoCityLinkMaybe';
import {
  formatListingEventDateTime,
  isTicketNetworkEventListing,
} from '../../util/listingEventDate';

/**
 * Ticket / inventory facts for the listing.
 * When `eventSeoMode` is true (event listing with SEO snapshot), skip identity
 * fields already covered by Event Snapshot / hero — keep seats & ticket type.
 */
const SectionListingInfo = ({
  publicData,
  listingConfig,
  description,
  eventSeoMode = false,
}) => {
  if (!publicData || !listingConfig) return null;

  const {
    eventName,
    seatNumbersIfAny,
    howManyTicketsAreSoldWithThisListing,
    headlinersAndArtists,
    musicGenreS,
    ticketType,
  } = publicData;

  const isTnListing = isTicketNetworkEventListing(publicData);
  const dateBox =
    formatListingEventDateTime({ publicData, description }) ||
    null;
  const venueDisplayName = venueDisplayNameFromPublicData(publicData);
  const cityDisplayName = cityDisplayNameFromPublicData(publicData);

  // Helper function to render details
  const renderDetail = (labelId, value) => (
    value ? (
      <div className={css.eventDetail}>
        <span className={css.boldText}>
          <FormattedMessage id={labelId} />
        </span>
        <span className={css.eventData}>{value}</span>
      </div>
    ) : null
  );

  const seatRow = renderDetail("SectionListingInfo.seatsNumber", seatNumbersIfAny);
  const soldRow = !isTnListing
    ? renderDetail("SectionListingInfo.ticketSold", howManyTicketsAreSoldWithThisListing)
    : null;
  const ticketTypeRow = renderDetail("SectionListingInfo.ticketType", ticketType);

  if (eventSeoMode) {
    if (!seatRow && !soldRow) {
      return null;
    }
    return (
      <div className={css.orderBoxWrapper}>
        {seatRow}
        {soldRow}
      </div>
    );
  }

  return (
    <div className={css.orderBoxWrapper}>
      {renderDetail("SectionListingInfo.eventName", eventName)}
      {renderDetail("SectionListingInfo.eventStart", dateBox)}
      {venueDisplayName ? (
        <div className={css.eventDetail}>
          <span className={css.boldText}>Venue</span>
          <SeoVenueLinkMaybe publicData={publicData} className={css.eventData}>
            {venueDisplayName}
          </SeoVenueLinkMaybe>
        </div>
      ) : null}
      {cityDisplayName ? (
        <div className={css.eventDetail}>
          <span className={css.boldText}>City</span>
          <SeoCityLinkMaybe publicData={publicData} className={css.eventData}>
            {cityDisplayName}
          </SeoCityLinkMaybe>
        </div>
      ) : null}
      {seatRow}
      {soldRow}
      {renderDetail("SectionListingInfo.headliners", headlinersAndArtists)}

      {Array.isArray(musicGenreS) && musicGenreS?.length > 0 && (
        <div className={css.eventDetail}>
          <span className={css.boldText}>
            <FormattedMessage id="SectionListingInfo.music" />
          </span>
          <div className={css.tagsBox}>
            {musicGenreS.map((genre, i) => (
              <span key={i} className={css.hashTags}>{genre}</span>
            ))}
          </div>
        </div>
      )}
      {ticketTypeRow}
    </div>
  );
};

export default SectionListingInfo;
